import { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import {
  AlertCircle,
  Inbox as InboxIcon,
  Loader2,
  LogIn,
  LogOut,
  MailOpen,
  RefreshCw
} from "lucide-react";
import { getMe, login, logout } from "./api.js";

function replacePath(path) {
  if (window.location.pathname !== path) {
    window.history.replaceState(null, "", path);
  }
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("tr-TR", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

function upsertNewest(list, email) {
  const rest = list.filter((item) => item.uid !== email.uid);
  return [email, ...rest].sort((a, b) => b.uid - a.uid);
}

function getInitials(str) {
  if (!str) return "?";
  const name = str.includes("<") ? str.split("<")[0].trim() : str.split("@")[0];
  const words = name.split(/[\s._-]+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(str) {
  const palette = [
    "#6366f1", "#8b5cf6", "#ec4899", "#14b8a6",
    "#f97316", "#06b6d4", "#84cc16", "#f43f5e",
    "#3b82f6", "#a855f7"
  ];
  if (!str) return palette[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function Avatar({ text, size = 38 }) {
  const initials = getInitials(text);
  const color = getAvatarColor(text || "?");
  return (
    <div
      aria-hidden="true"
      className="avatar"
      style={{
        backgroundColor: `${color}20`,
        color,
        fontSize: `${Math.round(size * 0.36)}px`,
        height: `${size}px`,
        width: `${size}px`
      }}
    >
      {initials}
    </div>
  );
}

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

export function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !containerRef.current) return;

    function renderWidget() {
      if (!window.turnstile || widgetIdRef.current !== null) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "light",
        size: "flexible",
        callback: (token) => setTsToken(token),
        "expired-callback": () => setTsToken(""),
        "error-callback": () => setTsToken("")
      });
    }

    if (window.turnstile) {
      renderWidget();
    } else {
      const timer = setInterval(() => {
        if (window.turnstile) {
          clearInterval(timer);
          renderWidget();
        }
      }, 50);
      return () => clearInterval(timer);
    }

    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  async function handleSubmit(event) {
    event.preventDefault();

    if (TURNSTILE_SITE_KEY && !tsToken) {
      setError("Lutfen robot dogrulamasini tamamlayin.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const result = await login(email, password, tsToken);
      onLogin(result.user);
    } catch (nextError) {
      setError(nextError.message);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTsToken("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-shell">
      <form className="login-panel" onSubmit={handleSubmit}>
        <div className="brand-mark">
          <InboxIcon aria-hidden="true" size={26} />
        </div>
        <div>
          <h1 className="login-title">OpssGamerShop</h1>
          <p className="login-subtitle">Posta kutunuza giris yapin</p>
        </div>
        <label>
          <span>Email</span>
          <input
            autoComplete="username"
            inputMode="email"
            name="email"
            onChange={(event) => setEmail(event.target.value.trim())}
            onPaste={(event) => {
              const text = event.clipboardData.getData("text");
              const colonIndex = text.indexOf(":");
              if (colonIndex !== -1) {
                event.preventDefault();
                setEmail(text.slice(0, colonIndex).trim());
                setPassword(text.slice(colonIndex + 1).trim());
              }
            }}
       
            placeholder="Mail Adresinizi Giriniz"
            required
            type="email"
            value={email}
          />
        </label>
        <label>
          <span>Parola</span>
          <input
            autoComplete="current-password"
            name="password"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
        </label>
        {TURNSTILE_SITE_KEY && (
          <div ref={containerRef} className="turnstile-container" />
        )}
        {error ? (
          <div className="form-error" role="alert">
            <AlertCircle aria-hidden="true" size={18} />
            <span>{error}</span>
          </div>
        ) : null}
        <button
          className="primary-button"
          disabled={submitting || (!!TURNSTILE_SITE_KEY && !tsToken)}
          type="submit"
        >
          {submitting ? (
            <Loader2 aria-hidden="true" className="spin" size={18} />
          ) : (
            <LogIn aria-hidden="true" size={18} />
          )}
          <span>Giris yap</span>
        </button>
      </form>
    </main>
  );
}

function StatusPill({ status }) {
  const isOnline = status === "connected";

  return (
    <div className={`status-pill ${isOnline ? "online" : "offline"}`}>
      <span className="status-dot" />
      <span>{isOnline ? "Bagli" : status === "loading" ? "Yukleniyor" : "Kopuk"}</span>
    </div>
  );
}

function MailList({ emails, selectedUid, onSelect }) {
  return (
    <>
      <div className="mail-list-header">
        <h2>Gelen Kutusu</h2>
        {emails.length > 0 && (
          <span className="mail-count-badge">{emails.length}</span>
        )}
      </div>
      {emails.length === 0 ? (
        <div className="empty-state">
          <InboxIcon aria-hidden="true" size={34} />
          <p>Inbox bos.</p>
        </div>
      ) : (
        <ul className="mail-list">
          {emails.map((email) => (
            <li key={email.uid}>
              <button
                className={`mail-row ${email.uid === selectedUid ? "selected" : ""}`}
                onClick={() => onSelect(email.uid)}
                type="button"
              >
                <Avatar text={email.from || "?"} size={38} />
                <div className="mail-row-content">
                  <div className="mail-row-top">
                    <span className="mail-subject">{email.subject}</span>
                    <span className="mail-date">{formatDate(email.date)}</span>
                  </div>
                  <span className="mail-from">{email.from || "Bilinmeyen gonderen"}</span>
                  <span className="mail-preview">{email.preview || "Onizleme yok"}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function MailIframe({ html }) {
  const ref = useRef(null);

  function handleLoad() {
    try {
      const doc = ref.current?.contentDocument;
      if (doc) {
        const height = doc.documentElement.scrollHeight;
        if (height > 0) {
          ref.current.style.height = `${height}px`;
        }
      }
    } catch {
      // sandbox may block access — CSS min-height handles this case
    }
  }

  return (
    <iframe
      ref={ref}
      className="mail-iframe"
      onLoad={handleLoad}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      srcDoc={html}
      title="Mail icerigi"
    />
  );
}

export function MailDetailPane({ detail, loading }) {
  if (loading) {
    return (
      <section className="detail-placeholder" aria-live="polite">
        <Loader2
          aria-hidden="true"
          className="spin"
          size={28}
          style={{ color: "var(--c-primary)", opacity: 0.65 }}
        />
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="detail-placeholder">
        <div className="detail-placeholder-icon">
          <MailOpen aria-hidden="true" size={30} />
        </div>
        <p>Okumak icin bir mail sec.</p>
      </section>
    );
  }

  return (
    <article className="mail-detail">
      <div className="mail-detail-header">
        <h2 className="mail-detail-subject">{detail.subject}</h2>
        <div className="mail-meta">
          <div className="mail-meta-row">
            <span className="mail-meta-label">Kimden</span>
            <span className="mail-meta-value">{detail.from || "−"}</span>
          </div>
          <div className="mail-meta-row">
            <span className="mail-meta-label">Kime</span>
            <span className="mail-meta-value">{detail.to || "−"}</span>
          </div>
          <div className="mail-meta-row">
            <span className="mail-meta-label">Tarih</span>
            <span className="mail-meta-value">{formatDate(detail.date) || "−"}</span>
          </div>
        </div>
      </div>
      <div className="mail-detail-body">
        {detail.bodyHtml ? (
          <MailIframe html={detail.bodyHtml} />
        ) : (
          <pre className="mail-body">{detail.bodyText || "Mail govdesi bos."}</pre>
        )}
      </div>
    </article>
  );
}

export function InboxPage({ user, onLogout }) {
  const [emails, setEmails] = useState([]);
  const [selectedUid, setSelectedUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const socket = useMemo(
    () =>
      io({
        autoConnect: false,
        withCredentials: true
      }),
    []
  );

  useEffect(() => {
    socket.connect();

    socket.on("connect", () => {
      setStatus("connected");
      socket.emit("mail:subscribe");
    });
    socket.on("disconnect", () => setStatus("disconnected"));
    socket.on("connect_error", () => {
      setStatus("disconnected");
      setError("Socket baglantisi kurulamadi.");
    });
    socket.on("mail:status", (payload) => {
      setStatus(payload.state);
      if (payload.state === "connected") {
        setRefreshing(false);
      }
    });
    socket.on("mail:list", (payload) => {
      setEmails(payload.emails ?? []);
      setRefreshing(false);
    });
    socket.on("mail:new", (payload) => {
      if (payload.email) {
        setEmails((current) => upsertNewest(current, payload.email));
      }
    });
    socket.on("mail:detail", (payload) => {
      setDetail(payload.email ?? null);
      setDetailLoading(false);
    });
    socket.on("mail:error", (payload) => {
      setError(payload.message || "Mail islemi tamamlanamadi.");
      setRefreshing(false);
      setDetailLoading(false);
    });
    socket.on("auth:expired", () => {
      void onLogout(true);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [onLogout, socket]);

  function handleSelect(uid) {
    setSelectedUid(uid);
    setDetailLoading(true);
    setDetail(null);
    socket.emit("mail:open", { uid });
  }

  function handleRefresh() {
    setRefreshing(true);
    setError("");
    socket.emit("mail:refresh");
  }

  return (
    <main className="inbox-shell">
      <header className="topbar">
        <div className="topbar-brand">
          <div className="topbar-brand-icon" aria-hidden="true">
            <InboxIcon size={18} />
          </div>
          <h1>OpssGamerShop</h1>
        </div>
        <div className="topbar-actions">
          <StatusPill status={status} />
          <div className="topbar-user">
            <Avatar text={user.email} size={26} />
            <span className="topbar-user-email">{user.email}</span>
          </div>
          <button
            aria-label="Yenile"
            className="icon-button"
            onClick={handleRefresh}
            title="Yenile"
            type="button"
          >
            <RefreshCw
              aria-hidden="true"
              className={refreshing ? "spin" : ""}
              size={16}
            />
          </button>
          <button
            aria-label="Cikis yap"
            className="icon-button"
            onClick={() => onLogout(false)}
            title="Cikis yap"
            type="button"
          >
            <LogOut aria-hidden="true" size={16} />
          </button>
        </div>
      </header>

      {error ? (
        <div className="inline-error" role="alert">
          <AlertCircle aria-hidden="true" size={16} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="inbox-layout">
        <aside className="mail-list-panel" aria-label="Mailler">
          <MailList emails={emails} selectedUid={selectedUid} onSelect={handleSelect} />
        </aside>
        <MailDetailPane detail={detail} loading={detailLoading} />
      </section>
    </main>
  );
}

export function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    getMe()
      .then((result) => {
        if (active) {
          setUser(result.user);
          replacePath("/inbox");
        }
      })
      .catch(() => {
        if (active) {
          setUser(null);
          replacePath("/login");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  async function handleLogout(expired) {
    if (!expired) {
      await logout().catch(() => {});
    }

    setUser(null);
    replacePath("/login");
  }

  function handleLogin(nextUser) {
    setUser(nextUser);
    replacePath("/inbox");
  }

  if (loading) {
    return (
      <main className="boot-screen">
        <Loader2 aria-hidden="true" className="spin" size={30} />
      </main>
    );
  }

  if (!user) {
    replacePath("/login");
    return <LoginPage onLogin={handleLogin} />;
  }

  return <InboxPage user={user} onLogout={handleLogout} />;
}
